import { TokenType } from "../enums/SDKTokenType";
import { BlurToken } from "../tokens/SDKBlurToken";
import { BorderToken } from "../tokens/SDKBorderToken";
import { ColorToken } from "../tokens/SDKColorToken";
import { BorderWidthToken, DimensionToken, DurationToken, FontSizeToken, LetterSpacingToken, LineHeightToken, OpacityToken, ParagraphSpacingToken, RadiusToken, SizeToken, SpaceToken, ZIndexToken, } from "../tokens/SDKDimensionToken";
import { GradientToken } from "../tokens/SDKGradientToken";
import { ShadowToken } from "../tokens/SDKShadowToken";
import { FontFamilyToken, FontWeightToken, ProductCopyToken, StringToken, } from "../tokens/SDKStringToken";
import { TextCaseToken } from "../tokens/SDKTextCaseToken";
import { TextDecorationToken } from "../tokens/SDKTextDecorationToken";
import { TypographyToken } from "../tokens/SDKTypographyToken";
import { VisibilityToken } from "../tokens/SDKVisibilityToken";
import cloneDeep from "lodash/cloneDeep";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definitions
const EMPTY_REMOTE_TOKEN_MODEL = {
    id: "",
    brandId: "",
    designSystemVersionId: "",
    persistentId: "",
    type: TokenType.color,
    meta: { name: "", description: "" },
    data: {},
    customPropertyOverrides: [],
};
/**
 * Builds an empty, value-less token instance. The value is left empty and the
 * metadata is populated afterwards by the replication logic
 */
const createValuelessToken = (TokenClass, versionId, emptyValue = null) => {
    // Token constructors expect a concrete value type, but replicas get their value later
    const TokenConstructor = TokenClass;
    return new TokenConstructor(versionId, EMPTY_REMOTE_TOKEN_MODEL, emptyValue, null, [], []);
};
/** Factory for each token type, producing an empty, value-less token instance */
const VALUELESS_TOKEN_FACTORIES = {
    [TokenType.blur]: (versionId) => createValuelessToken(BlurToken, versionId),
    [TokenType.border]: (versionId) => createValuelessToken(BorderToken, versionId),
    [TokenType.color]: (versionId) => createValuelessToken(ColorToken, versionId),
    [TokenType.gradient]: (versionId) => createValuelessToken(GradientToken, versionId, []),
    [TokenType.dimension]: (versionId) => createValuelessToken(DimensionToken, versionId),
    [TokenType.size]: (versionId) => createValuelessToken(SizeToken, versionId),
    [TokenType.space]: (versionId) => createValuelessToken(SpaceToken, versionId),
    [TokenType.opacity]: (versionId) => createValuelessToken(OpacityToken, versionId),
    [TokenType.fontSize]: (versionId) => createValuelessToken(FontSizeToken, versionId),
    [TokenType.lineHeight]: (versionId) => createValuelessToken(LineHeightToken, versionId),
    [TokenType.letterSpacing]: (versionId) => createValuelessToken(LetterSpacingToken, versionId),
    [TokenType.paragraphSpacing]: (versionId) => createValuelessToken(ParagraphSpacingToken, versionId),
    [TokenType.borderWidth]: (versionId) => createValuelessToken(BorderWidthToken, versionId),
    [TokenType.radius]: (versionId) => createValuelessToken(RadiusToken, versionId),
    [TokenType.duration]: (versionId) => createValuelessToken(DurationToken, versionId),
    [TokenType.zIndex]: (versionId) => createValuelessToken(ZIndexToken, versionId),
    [TokenType.shadow]: (versionId) => createValuelessToken(ShadowToken, versionId, []),
    [TokenType.string]: (versionId) => createValuelessToken(StringToken, versionId),
    [TokenType.productCopy]: (versionId) => createValuelessToken(ProductCopyToken, versionId),
    [TokenType.fontFamily]: (versionId) => createValuelessToken(FontFamilyToken, versionId),
    [TokenType.fontWeight]: (versionId) => createValuelessToken(FontWeightToken, versionId),
    [TokenType.typography]: (versionId) => createValuelessToken(TypographyToken, versionId),
    [TokenType.textCase]: (versionId) => createValuelessToken(TextCaseToken, versionId),
    [TokenType.textDecoration]: (versionId) => createValuelessToken(TextDecorationToken, versionId),
    [TokenType.visibility]: (versionId) => createValuelessToken(VisibilityToken, versionId),
};
const createValuelessTokenOfType = (tokenType, versionId) => {
    const factory = VALUELESS_TOKEN_FACTORIES[tokenType];
    if (!factory) {
        throw new Error(`Unsupported token type: ${tokenType}`);
    }
    return factory(versionId);
};
const copyCommonTokenMetadata = (replica, token) => {
    replica.id = token.id;
    replica.idInVersion = token.idInVersion;
    replica.brandId = token.brandId;
    replica.designSystemVersionId = token.designSystemVersionId;
    replica.name = token.name;
    replica.description = token.description;
    replica.tokenType = token.tokenType;
    replica.parentGroupId = token.parentGroupId;
    replica.sortOrder = token.sortOrder;
    replica.properties = token.properties;
    replica.propertyValues = token.propertyValues;
    replica.createdAt = token.createdAt;
    replica.updatedAt = token.updatedAt;
    replica.resolutionState = token.resolutionState;
};
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class ThemeUtilities {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Helpers
    /** Creates duplicate of the token */
    static replicateTokenAsThemePrefabWithoutValue(token, themeId, origin, versionId) {
        const replica = createValuelessTokenOfType(token.tokenType, versionId);
        copyCommonTokenMetadata(replica, token);
        replica.themeId = themeId; // Assign just-created theme
        replica.origin = origin !== null && origin !== void 0 ? origin : null; // Re-direct origin to the other origin
        // No replication of value - value will get replaced by the override
        return replica;
    }
    /** Creates duplicate of the token */
    static replicateTokenWithoutValue(token) {
        const replica = createValuelessTokenOfType(token.tokenType, "");
        copyCommonTokenMetadata(replica, token);
        replica.themeId = token.themeId;
        replica.collectionId = token.collectionId;
        replica.tokenPath = token.tokenPath;
        replica.origin = token.origin;
        // No replication of value - value will get replaced by the override
        return replica;
    }
    /** Creates duplicate of the token */
    static replicateTokenWithValue(token) {
        const replica = this.replicateTokenWithoutValue(token);
        replica.value = cloneDeep(token.value);
        return replica;
    }
}
